import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  TablePagination,
  Snackbar,
  Alert,
  Tooltip,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Campaign';
import CloseIcon from '@mui/icons-material/Close';
import { getMembers, sendMemberNotifications } from '../api';
import { useAuth } from '../context/AuthContext';

const membersListState = {
  pageSize: 10,
  pageNumber: 0,
  sortBy: 'last_name',
  sortOrder: 'asc',
  filterMen: true,
  filterWomen: true,
  formFilter: '',
  ageType: '',
  filterExcludedOnly: false,
};

function formatPhone(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

export default function MembersPage() {
  const [searchParams] = useSearchParams();
  const maleParam = searchParams.get('male');
  const femaleParam = searchParams.get('female');
  const formFilterParam = searchParams.get('form_filter');
  const typeParam = searchParams.get('type');

  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState(() => membersListState.sortBy);
  const [sortOrder, setSortOrder] = useState(() => membersListState.sortOrder);
  const [page, setPage] = useState(() => membersListState.pageNumber);
  const [pageSize, setPageSize] = useState(() => membersListState.pageSize);
  const [filterMen, setFilterMen] = useState(maleParam !== null ? maleParam === '1' : membersListState.filterMen);
  const [filterWomen, setFilterWomen] = useState(femaleParam !== null ? femaleParam === '1' : membersListState.filterWomen);
  const [formFilter, setFormFilter] = useState(formFilterParam || membersListState.formFilter);
  const [ageType, setAgeType] = useState(typeParam || membersListState.ageType);
  const [filterExcludedOnly, setFilterExcludedOnly] = useState(membersListState.filterExcludedOnly);
  const gender = filterMen && filterWomen ? '' : filterMen ? 'male' : filterWomen ? 'female' : '';
  const isCompleted = formFilter === 'completed' ? '1' : formFilter === 'no_form' ? '0' : undefined;
  const staleOnly = formFilter === 'stale';
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [sendingMemberId, setSendingMemberId] = useState(null);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const { data, meta } = await getMembers({
        search: search || undefined,
        page: page + 1,
        pageSize,
        sortBy,
        sortOrder,
        gender: gender || undefined,
        type: ageType || undefined,
        isCompleted,
        staleOnly: staleOnly || undefined,
        excludedOnly: filterExcludedOnly || undefined,
      });
      setMembers(data);
      setTotal(meta?.total ?? 0);
    } catch (err) {
      console.error(err);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [search, sortBy, sortOrder, page, pageSize, gender, ageType, isCompleted, staleOnly, filterExcludedOnly]);

  useEffect(() => {
    const timer = setTimeout(load, 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    membersListState.pageSize = pageSize;
    membersListState.pageNumber = page;
    membersListState.sortBy = sortBy;
    membersListState.sortOrder = sortOrder;
    membersListState.filterMen = filterMen;
    membersListState.filterWomen = filterWomen;
    membersListState.formFilter = formFilter;
    membersListState.ageType = ageType;
    membersListState.filterExcludedOnly = filterExcludedOnly;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSize, page, sortBy, sortOrder, filterMen, filterWomen, formFilter, ageType, filterExcludedOnly]);

  const handleSort = (field) => {
    const isAsc = sortBy === field && sortOrder === 'asc';
    setSortBy(field);
    setSortOrder(isAsc ? 'desc' : 'asc');
    setPage(0);
  };

  const handleViewInvolvements = (member) => {
    navigate(`/admin/members/${member.id}/preferences`);
  };

  const handleEditInvolvements = (member) => {
    navigate(`/admin/members/${member.id}/preferences/edit`);
  };

  const handleSendNotifications = async (member) => {
    setSendingMemberId(member.id);
    try {
      const result = await sendMemberNotifications(member.id);
      const parts = [];
      if (result.email_sent) parts.push('email');
      if (result.sms_sent) parts.push('SMS');
      const msg = parts.length
        ? `Notification sent (${parts.join(', ')}) to ${member.first_name} ${member.last_name}`
        : 'No channels available';
      setSnackbar({ open: true, message: result.success ? msg : result.errors?.[0]?.error || msg, severity: result.success ? 'success' : 'warning' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to send notification', severity: 'error' });
    } finally {
      setSendingMemberId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">Members</Typography>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel sx={{ overflow: 'visible' }}>Age</InputLabel>
            <Select
              value={ageType}
              label="Age"
              onChange={(e) => { setAgeType(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="adult">Adult</MenuItem>
              <MenuItem value="teenager">Teenager</MenuItem>
              <MenuItem value="child">Child</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={filterMen}
                onChange={(e) => { setFilterMen(e.target.checked); setPage(0); }}
                size="small"
              />
            }
            label="Men"
            sx={{ whiteSpace: 'nowrap' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterWomen}
                onChange={(e) => { setFilterWomen(e.target.checked); setPage(0); }}
                size="small"
              />
            }
            label="Women"
            sx={{ whiteSpace: 'nowrap' }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ overflow: 'visible' }}>Forms</InputLabel>
            <Select
              value={formFilter}
              label="Forms"
              onChange={(e) => { setFormFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="completed">Form Completed</MenuItem>
              <MenuItem value="no_form">No Form</MenuItem>
              <MenuItem value="stale">Out of Date</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={filterExcludedOnly}
                onChange={(e) => { setFilterExcludedOnly(e.target.checked); setPage(0); }}
                size="small"
              />
            }
            label="Excluded only"
            sx={{ whiteSpace: 'nowrap' }}
          />
          <TextField
          size="small"
          placeholder="Search by name, phone, or email..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={sortBy === 'last_name' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'last_name'}
                  direction={sortBy === 'last_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('last_name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'email' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'email'}
                  direction={sortBy === 'email' ? sortOrder : 'asc'}
                  onClick={() => handleSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'phone' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'phone'}
                  direction={sortBy === 'phone' ? sortOrder : 'asc'}
                  onClick={() => handleSort('phone')}
                >
                  Phone
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'gender' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'gender'}
                  direction={sortBy === 'gender' ? sortOrder : 'asc'}
                  onClick={() => handleSort('gender')}
                >
                  Gender
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'type' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'type'}
                  direction={sortBy === 'type' ? sortOrder : 'asc'}
                  onClick={() => handleSort('type')}
                >
                  Age
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'dob' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'dob'}
                  direction={sortBy === 'dob' ? sortOrder : 'asc'}
                  onClick={() => handleSort('dob')}
                >
                  Date of Birth
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={sortBy === 'ministries_count' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'ministries_count'}
                  direction={sortBy === 'ministries_count' ? sortOrder : 'asc'}
                  onClick={() => handleSort('ministries_count')}
                >
                  Ministries
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={sortBy === 'ministry_areas_count' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'ministry_areas_count'}
                  direction={sortBy === 'ministry_areas_count' ? sortOrder : 'asc'}
                  onClick={() => handleSort('ministry_areas_count')}
                >
                  Ministry Areas
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sortBy === 'form_completed_at' ? sortOrder : false}>
                <TableSortLabel
                  active={sortBy === 'form_completed_at'}
                  direction={sortBy === 'form_completed_at' ? sortOrder : 'asc'}
                  onClick={() => handleSort('form_completed_at')}
                >
                  Completed
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">Excluded</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11}>Loading...</TableCell>
              </TableRow>
            ) : (
              members.map((m) => (
                <TableRow key={m.id}>
                  <TableCell>
                    {m.first_name} {m.last_name}
                  </TableCell>
                  <TableCell>{m.email}</TableCell>
                  <TableCell>{formatPhone(m.phone)}</TableCell>
                  <TableCell>{m.gender}</TableCell>
                  <TableCell>{m.type}</TableCell>
                  <TableCell>
                    {m.dob ? new Date(m.dob + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                  </TableCell>
                  <TableCell align="right">{m.ministries_count ?? 0}</TableCell>
                  <TableCell align="right">{m.ministry_areas_count ?? 0}</TableCell>
                  <TableCell>
                    {m.form_completed_at
                      ? new Date(m.form_completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : ''}
                  </TableCell>
                  <TableCell align="center">
                    {m.is_excluded && (
                      <Tooltip title="Excluded from form">
                        <CloseIcon sx={{ color: 'error.main', fontSize: 20 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View involvements">
                      <IconButton size="small" onClick={() => handleViewInvolvements(m)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    {isAdmin && (
                      <Tooltip title="Edit involvements">
                        <IconButton size="small" onClick={() => handleEditInvolvements(m)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isAdmin && (
                      <Tooltip title={m.email || m.phone ? 'Send form reminder (email & SMS)' : 'No email or phone'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleSendNotifications(m)}
                            disabled={(!m.email && !m.phone) || sendingMemberId === m.id}
                            title="Send Notification"
                          >
                            <SendIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Rows per page:"
        />
      </TableContainer>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
